<template>
  <div class="position-relative">
    <share-collapse :entity="item" />
    <!-- General Item Information Portlet -->
    <basic-portlet :title="getItemLabel(true) + ' Information'" :icon="edit ? 'fa fa-edit' : 'fa fa-plus'">
      <item-info-form :item="item" />
    </basic-portlet>

    <!-- Episode Details Portlet -->
    <collapse-portlet v-if="item && item.uuid" title="Episode Details"
                      icon="la la-share-alt"
                      :expanded="false">
      <div class="row">
        <div class="col-6">
          <div class="form-group">
            <label class="col-3 col-form-label">Inherit from Feed</label>
            <div class="col-3">
              <span class="kt-switch">
                <label>
                  <input v-model="inherit_episode_details" type="checkbox"
                         checked="checked"
                         name="">
                  <span />
                </label>
              </span>
            </div>
            <span class="kt-form__help">If enabled, episode details will be populated based on feed information</span>
          </div>
          <button v-if="can_remove_episode_detail" class="btn btn-ss-danger" @click="deleteEpisodeDetails()">
            Save Changes
          </button>
        </div>
      </div>

      <div v-show="! inherit_episode_details" class="row">
        <div class="col-12">
          <episode-details-form :item="item" />
        </div>
      </div>
    </collapse-portlet>

    <meta-data v-if="item && item.storage" :storage-uuid="item.storage.uuid" />

    <sticky-control-bar>
      <button v-if="selected_storage_item || edit"
              class="btn item-save-btn btn-ss-standout"
              :disabled="loading"
              @click.prevent="save">
        <span>
          <loader v-if="loading" small class="mr-2" />
          <i v-else class="la la-save" />
          <span v-if="edit">Update</span>
          <span v-else>Save</span>
        </span>
      </button>
      <button v-if="edit"
              class="btn btn-ss"
              :disabled="loading"
              @click="assignToFeed">
        <loader v-if="loading" small class="mr-2" />
        <i v-else class="la la-link" /> Assign to Feed
      </button>
      <button type="button"
              class="btn btn-ss"
              :disabled="loading"
              @click="duplicateItem">
        <loader v-if="loading" small class="mr-2" />
        <i v-else class="la la-copy" /> Duplicate
      </button>
    </sticky-control-bar>

    <assign-to-feed-modal name="assign-to-feed-modal" />
  </div>
</template>

<script>
import "vue-datetime/dist/vue-datetime.css";
import ShareCollapse from "VuePartials/App/Item/ShareCollapse";
import {
  formatBytes,
} from 'Helpers/formatters';

import CollapsePortlet from 'VueComponents/CollapsePortlet.vue';
import SocialShareMixin from 'VueMixins/SocialShareMixin';
import EpisodeDetailsForm from "VuePartials/App/Item/EpisodeDetailsForm";
import ItemInfoForm from "VuePartials/App/Item/ItemInfoForm";
import AssignToFeedModal from 'VueModals/AssignToFeedModal';
import BasicPortlet from "VueComponents/BasicPortlet";
import MetaData from "VuePartials/App/Ffmpeg/MetadataForm";

import StickyControlBar from 'VueComponents/StickyControlBar';

export default {
  components: {
    MetaData,
    BasicPortlet,
    ShareCollapse,
    CollapsePortlet,
    EpisodeDetailsForm,
    ItemInfoForm,
    AssignToFeedModal,
    StickyControlBar,
  },
  mixins: [SocialShareMixin],
  data: () => ({
    user: window.user,
    original_item: {},
    item: window.item ?? {},
    edit: !!(window.item && window.item.uuid),
    show_episode_details: false,
    feeds: [],
    selected_feed_for_assigning: null,
    inherit_episode_details: true,
    selected_storage_item: null,
    loading: false,
  }),
  computed: {
    can_remove_episode_detail() {
      // If item DID have episode detail but it inherit has been toggled off.
      return this.inherit_episode_details && this.item.episode_detail && this.item.episode_detail.guid;
    },
  },
  beforeMount() {
    if (this.item && this.item.episode_detail && this.item.episode_detail.guid) {
      this.inherit_episode_details = false;
    }
  },
  mounted() {
    EventBus.$on('item-updated', (payload) => {
      this.item = payload;
    });

    EventBus.$on('selected-storage-item', (ev) => {
      this.selected_storage_item = ev.selected_storage_item;
    });
  },
  methods: {
    async assignToFeed() {
      this.loading = true;
      const modal_result = await this.fireModal(`assign-to-feed-modal`);
      if (modal_result.data.confirm) {
        const feed = modal_result.data.feed;
        try {
          const res = (await axios.post(`/api/v1/feeds/${feed.uuid}/attachItems`, {
            item_uuids: [this.item.uuid],
          })).data;
          if (res.success) {
            this.feed = res.data.data;
            EventBus.$emit('item-feeds-updated');
            toastr.success(`Successfully assigned to Feed ${feed.name}!`);
          } else {
            toastr.error(`Unable to assign to Feed ${feed.name}.`);
          }
        } catch (err) {
          toastr.error(err);
          console.error(err);
        }
      }
      this.loading = false;
    },
    async deleteEpisodeDetails() {
      this.loading = true;
      try {
        await axios.delete(`/api/v1/items/${this.item.uuid}/episode-detail`).then((response) => {
          this.item = response.data.data;
          this.inherit_episode_details = true;
          toastr.success("Episode Details Deleted");
        });
      } catch (err) {
        window.handleAxiosError(err);
      }
      this.loading = false;
    },
    async duplicateItem() {
      if (!this.item || !this.item.uuid) {
        toastr.error("Something went wrong. Please try again later");
        return false;
      }

      this.loading = true;
      try {
        const response = await axios.post(`/api/v1/items/${this.item.uuid}/duplicate`);
        if (response.data.data && response.data.data.item) {
          const new_item = response.data.data.item;
          toastr.success("Duplicated Item. Redirecting...");
          window.location.href = `/items/${new_item.uuid}/edit`;
        }
      } catch (err) {
        window.handleAxiosError(err);
      }
      this.loading = false;
    },
    formatFileSize(size) {
      return formatBytes(size, 2);
    },
    async save() {
      this.loading = true;
      EventBus.$emit('item-save'); // ItemInfoForm listens for this
      // we listen for the 'item-updated' event OR for 10s to pass before unsetting the loading state
      await Promise.race([
        new Promise(res => EventBus.$on('item-updated', res)),
        new Promise(res => setTimeout(res, 10000)),
      ]);
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:map";

@use "~AssetsSass/_variables.scss" as *;
@use "~AssetsSass/_mixins.scss" as *;

@include metronic-like-vti;

.autocomplete {
  position:inherit !important;
  .item {
    background-color: #fff;
    width:100% !important;
    color: inherit;
    margin-bottom: 0;
    &:hover {
      background-color: map.get($ss, 'primary');
    }
  }
}

label {
  font-weight:bold;
}

.multiselect__single, .option__desc {
  -ms-word-break: break-all;
  word-break: break-all;
}

.artwork-multiselct {
  img {
    display:block;
    float:left;
    margin-right:10px;
    max-height: 50px;
  }
  .option__desc {
    display:block; float:left;
  }
}

.publish-info {
  border-top:1px solid #ebedf2;
}
</style>
